/* eslint-disable camelcase */
/* eslint-disable react/display-name */
/**
 * @file PatientEventsView.jsx
 * @author Fernando Ruano
 * @description Patient Events View Page
 */

// Dependencies
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router';

// Components
import DashboardCard from 'components/DashboardCard';
import DashboardTitle from 'components/Charts/DashboardTitle';
import ProgressBar from 'components/Charts/ProgressBar';
import EmptyChart from 'components/EmptyChart';

// Commons
import styleGuideColors from 'commons/styleGuideColors';

// Helpers
import { transformToPercent } from '../helper';

/**
 * PatientEvents View
 * @param { Object } dashboardStaffActions - DashboardStaff Actions.
 * @return { Node } React node.
 */

function PatientEventsView({ dashboardStaffActions, clinicId }) {
  const { t: translate } = useTranslation();
  const history = useHistory();
  const [chartData, setChartData] = useState(null);

  const { getPatientEvents } = dashboardStaffActions;

  const onGetData = (_, resp) => {
    const data = [];
    const colors = {
      Hospitalización: styleGuideColors.alertError,
      'Cambio de Domicilio': styleGuideColors.graphYellow,
      'Cambio de Asegurador': styleGuideColors.baxterBrandCyan,
      'Causal de inasistencia': styleGuideColors.baxterBrandBaxterBlue,
      'Situacion Familiar': styleGuideColors.baxterBrandPurple,
      'Dificultad de contacto telefónico con la clínica':
        styleGuideColors.baxterBrandTeal,
      'Cambio de Telefono de Contacto': styleGuideColors.baxterBrandGreen
    };

    if (resp?.data) {
      const newData = transformToPercent(resp?.data, 'cantidad', 'value');
      newData.forEach(({ cantidad, evento }) => {
        const item = {
          title: cantidad,
          subtitle: evento,
          percent: cantidad,
          color: colors[evento]
        };
        switch (evento) {
          case 'Hospitalización':
            data[0] = item;
            break;
          case 'Cambio de Domicilio':
            data[1] = item;
            break;
          case 'Cambio de Asegurador':
            data[2] = item;
            break;
          case 'Causal de inasistencia':
            data[3] = item;
            break;
          case 'Situacion Familiar':
            data[4] = item;
            break;
          case 'Dificultad de contacto telefónico con la clínica':
            data[5] = item;
            break;
          case 'Cambio de Telefono de Contacto':
            data[6] = item;
            break;
          default:
            data[7] = item;
            break;
        }
      });
      setChartData(data.filter((item) => item));
    } else {
      setChartData([]);
    }
  };

  useEffect(() => {
    setChartData(null);
    getPatientEvents({ clinicId }, onGetData);
  }, [clinicId, translate]);

  const handleDetailClick = (index) => {
    // Get data by index and send by url param to detail list
    const { title: totalItems, subtitle: classification } = chartData[index];
    history.push(
      `/main/dashboard-detail/patient-events/${classification}/${totalItems}`
    );
  };

  return (
    <DashboardCard id="F41" loading={!chartData}>
      <div className="flex flex-wrap">
        <DashboardTitle title={translate('dashboardStaff.patientEvents')} />
        <div className="w-full">
          {chartData?.length === 0 && <EmptyChart />}
          <div className="w-full grid grid-cols-2">
            <div
              className="w-full px-2"
              style={{ borderRight: '1px solid #ebeff7' }}>
              {chartData
                ?.slice(0, 4)
                .map(({ color, title, subtitle, percent }, index) => (
                  <div
                    onClick={() => handleDetailClick(index)}
                    key={index}
                    className="mt-4">
                    <ProgressBar
                      color={color}
                      title={title}
                      subtitle={subtitle}
                      percent={percent}
                      showPercent={false}
                    />
                  </div>
                ))}
            </div>
            <div className="w-full px-2">
              {chartData
                ?.slice(4, chartData?.length)
                .map(({ color, title, subtitle, percent }, index) => (
                  <div
                    onClick={() => handleDetailClick(index + 4)}
                    key={index}
                    className="mt-4">
                    <ProgressBar
                      color={color}
                      title={title}
                      subtitle={subtitle}
                      percent={percent}
                      showPercent={false}
                    />
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
    </DashboardCard>
  );
}

/**
 * Component definition of proptypes.
 */
PatientEventsView.propTypes = {
  dashboardStaffActions: PropTypes.shape({
    getPatientEvents: PropTypes.func.isRequired
  }),
  clinicId: PropTypes.any
};

export default PatientEventsView;
